import type {DataViewerFilterClearConfig} from "~/components/ottopilotDataViewer/dataViewerFilterClearConfig";
import {
    applyCreatedAtDateRangeToFilters,
    DATA_VIEWER_CREATED_AT_DATE_RANGE_COLUMN_ID,
    mergeDefaultCreatedAtMonthFilter,
    readCreatedAtDateRangeFromFilters,
    shouldApplyDefaultCreatedAtMonthFilter,
    stripAutoDefaultCreatedAtDateRangeFromLineItems,
    stripCreatedAtDateRangeLineItems,
} from "~/components/ottopilotDataViewer/dataViewerDateRangeDefaults";
import type {FilterConfiguration, FilterConfigurationLineItem} from "~/components/ottopilotDataViewer/filterConfiguration";
import {isPreservedFilterLineItem} from "~/components/ottopilotDataViewer/filterLineItemEquality";
import {
    applyJourneyScopeSelectionToFilterConfiguration,
    getJourneyScopeSelectionFromFilter,
    isJourneyScopeManagedFilterLineItem,
    type JourneyScopeFilterConfig,
    type JourneyScopeSelection,
} from "~/components/ottopilotDataViewer/journeyScopeFilterConfiguration";
import type {Iso8601DateTime} from "@vesta/common-type-definitions/typeDefinitions";
import type {SearchConfiguration} from "~/components/ottopilotDataViewer/searchConfiguration";
import type {SortConfiguration} from "~/components/ottopilotDataViewer/sortConfiguration";
import {generateUuid} from "@vesta/global-common-typescript/utilities/utilities";

export type DataViewerFilterPersistenceV1 = {
    version: 1;
    filterLineItems: Array<FilterConfigurationLineItem>;
    visibleFilterColumnIds: Array<string>;
    searchValue: string;
    sortLineItems: SortConfiguration["lineItems"];
};

export function buildDataViewerFilterPersistenceStorageKey(
    queryKeyPrefix: ReadonlyArray<string | null | undefined>,
): string {
    const segments = queryKeyPrefix.filter(
        (segment): segment is string => segment !== null && segment !== undefined && segment !== "",
    );
    return `ottopilot:data-viewer-filters:v1:${segments.join(":")}`;
}

function isFilterLineItem(value: unknown): value is FilterConfigurationLineItem {
    if (value === null || typeof value !== "object") {
        return false;
    }
    const record = value as Record<string, unknown>;
    return typeof record.columnId === "string" && typeof record.operation === "string";
}

export function loadDataViewerFilterPersistence(storageKey: string): DataViewerFilterPersistenceV1 | null {
    if (typeof window === "undefined") {
        return null;
    }
    try {
        const raw = window.localStorage.getItem(storageKey);
        if (raw === null || raw === "") {
            return null;
        }
        const parsed: unknown = JSON.parse(raw);
        if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
            return null;
        }
        const record = parsed as Record<string, unknown>;
        if (record.version !== 1) {
            return null;
        }
        const filterLineItems = Array.isArray(record.filterLineItems)
            ? record.filterLineItems.filter(isFilterLineItem)
            : [];
        const visibleFilterColumnIds = Array.isArray(record.visibleFilterColumnIds)
            ? record.visibleFilterColumnIds.filter((id): id is string => typeof id === "string")
            : [];
        const searchValue = typeof record.searchValue === "string" ? record.searchValue : "";
        const sortLineItems = Array.isArray(record.sortLineItems) ? record.sortLineItems : [];
        if (
            filterLineItems.length === 0 &&
            visibleFilterColumnIds.length === 0 &&
            searchValue === "" &&
            sortLineItems.length === 0
        ) {
            return null;
        }
        return {
            version: 1,
            filterLineItems,
            visibleFilterColumnIds,
            searchValue,
            sortLineItems,
        };
    } catch {
        return null;
    }
}

export function saveDataViewerFilterPersistence(
    storageKey: string,
    state: DataViewerFilterPersistenceV1,
    dateRangeColumnId: string = DATA_VIEWER_CREATED_AT_DATE_RANGE_COLUMN_ID,
): void {
    if (typeof window === "undefined") {
        return;
    }
    try {
        const sanitizedState: DataViewerFilterPersistenceV1 = {
            ...state,
            visibleFilterColumnIds: stripDateRangeFromVisibleFilterColumnIds(state.visibleFilterColumnIds, dateRangeColumnId),
        };
        window.localStorage.setItem(storageKey, JSON.stringify(sanitizedState));
    } catch {
        // quota / private mode
    }
}

export function clearDataViewerFilterPersistence(storageKey: string): void {
    if (typeof window === "undefined") {
        return;
    }
    try {
        window.localStorage.removeItem(storageKey);
    } catch {
        // ignore
    }
}

function getBaselineColumnIdSet(baselineLineItems: Array<FilterConfigurationLineItem>): Set<string> {
    return new Set(baselineLineItems.map((lineItem) => lineItem.columnId));
}

function stripJourneyScopeManagedFilterLineItems(
    lineItems: Array<FilterConfigurationLineItem>,
    journeyScopeFilterConfig: JourneyScopeFilterConfig | undefined,
): Array<FilterConfigurationLineItem> {
    if (journeyScopeFilterConfig === undefined) {
        return lineItems;
    }
    return lineItems.filter(
        (lineItem) => isJourneyScopeManagedFilterLineItem(lineItem, journeyScopeFilterConfig) === false,
    );
}

function stripFilterLineItemsOnColumns(
    lineItems: Array<FilterConfigurationLineItem>,
    columnIds: ReadonlySet<string>,
): Array<FilterConfigurationLineItem> {
    if (columnIds.size === 0) {
        return lineItems;
    }
    return lineItems.filter((lineItem) => columnIds.has(lineItem.columnId) === false);
}

/** Removes sidebar-owned status column filters before writing to localStorage. */
export function stripModuleStatusManagedFilterLineItemsForPersistence(
    lineItems: Array<FilterConfigurationLineItem>,
    moduleStatusManagedColumnIds: ReadonlyArray<string> | undefined,
): Array<FilterConfigurationLineItem> {
    if (moduleStatusManagedColumnIds === undefined || moduleStatusManagedColumnIds.length === 0) {
        return lineItems;
    }
    return stripFilterLineItemsOnColumns(lineItems, new Set(moduleStatusManagedColumnIds));
}

/** Strips journey scope and sidebar status column filters before persisting filter line items. */
export function stripManagedFilterLineItemsForPersistence(
    lineItems: Array<FilterConfigurationLineItem>,
    args: {
        journeyScopeFilterConfig?: JourneyScopeFilterConfig;
        moduleStatusManagedColumnIds?: ReadonlyArray<string>;
    },
): Array<FilterConfigurationLineItem> {
    const withoutJourneyScope = stripJourneyScopeManagedFilterLineItems(
        lineItems,
        args.journeyScopeFilterConfig,
    );
    return stripModuleStatusManagedFilterLineItemsForPersistence(
        withoutJourneyScope,
        args.moduleStatusManagedColumnIds,
    );
}

export type PersistedCreatedAtDateRange = {
    from: Iso8601DateTime;
    to: Iso8601DateTime;
};

/** Merge persisted user filters with module mandatory baseline (preserved / initial filters). */
export function resolveFilterConfigurationFromPersistence({
    dateRangeColumnId = DATA_VIEWER_CREATED_AT_DATE_RANGE_COLUMN_ID,
    persisted,
    initialFilterConfiguration,
    filterClearConfig,
    defaultCreatedAtMonthFilter,
    journeyScopeFilterConfig,
    persistedJourneyScopeSelection,
    persistedCreatedAtDateRange,
    moduleStatusManagedColumnIds,
    skipJourneyScopeReapply = false,
}: {
    dateRangeColumnId?: string;
    persisted: DataViewerFilterPersistenceV1 | null;
    initialFilterConfiguration: FilterConfiguration | undefined;
    filterClearConfig: DataViewerFilterClearConfig | undefined;
    defaultCreatedAtMonthFilter: boolean;
    journeyScopeFilterConfig?: JourneyScopeFilterConfig;
    /** From v2 blob; ignored when `skipJourneyScopeReapply` is true (sidebar status child view). */
    persistedJourneyScopeSelection?: JourneyScopeSelection;
    /** From v2 blob or persisted filter line items; overrides config defaultApplied date bounds. */
    persistedCreatedAtDateRange?: PersistedCreatedAtDateRange | null;
    /** Status column ids owned by sidebar nav — never restored from blob. */
    moduleStatusManagedColumnIds?: ReadonlyArray<string>;
    /** When true, do not re-apply journey scope from blob or config (sidebar owns the status column). */
    skipJourneyScopeReapply?: boolean;
}): FilterConfiguration {
    const moduleStatusColumnIdSet = new Set(moduleStatusManagedColumnIds ?? []);
    const baselineLineItemsRaw = initialFilterConfiguration?.lineItems ?? [];
    const baselineLineItemsWithoutDateRange = stripCreatedAtDateRangeLineItems(
        stripJourneyScopeManagedFilterLineItems(baselineLineItemsRaw, journeyScopeFilterConfig),
        dateRangeColumnId,
    );
    const baselineLineItems = baselineLineItemsWithoutDateRange;
    const preservedColumnIds = new Set(filterClearConfig?.preservedFilterLineItemColumnIds ?? []);
    const preservedLineItems = filterClearConfig?.preservedFilterLineItems ?? [];
    const baselineColumnIds = getBaselineColumnIdSet(baselineLineItems);

    const applyDefaultCreatedAtMonthFilter = shouldApplyDefaultCreatedAtMonthFilter(
        defaultCreatedAtMonthFilter,
    );

    const createdAtDateRangeFromPersistedLineItems: [Iso8601DateTime | null, Iso8601DateTime | null] | null =
        persisted === null ? null : readCreatedAtDateRangeFromFilters(persisted.filterLineItems, dateRangeColumnId);
    const resolvedCreatedAtDateRange: PersistedCreatedAtDateRange | null =
        persistedCreatedAtDateRange !== undefined
            ? persistedCreatedAtDateRange
            : createdAtDateRangeFromPersistedLineItems !== null &&
                createdAtDateRangeFromPersistedLineItems[0] !== null &&
                createdAtDateRangeFromPersistedLineItems[1] !== null
              ? {
                    from: createdAtDateRangeFromPersistedLineItems[0],
                    to: createdAtDateRangeFromPersistedLineItems[1],
                }
              : null;

    const persistedUserLineItemsRaw =
        persisted === null
            ? []
            : stripCreatedAtDateRangeLineItems(
                  stripModuleStatusManagedFilterLineItemsForPersistence(
                      stripJourneyScopeManagedFilterLineItems(
                          persisted.filterLineItems,
                          journeyScopeFilterConfig,
                      ),
                      moduleStatusManagedColumnIds,
                  ),
                  dateRangeColumnId,
              ).filter(
                  (lineItem) =>
                      baselineColumnIds.has(lineItem.columnId) === false &&
                      preservedColumnIds.has(lineItem.columnId) === false &&
                      isPreservedFilterLineItem(lineItem, preservedLineItems) === false &&
                      moduleStatusColumnIdSet.has(lineItem.columnId) === false,
              );
    const persistedUserLineItems = stripAutoDefaultCreatedAtDateRangeFromLineItems(
        persistedUserLineItemsRaw,
        applyDefaultCreatedAtMonthFilter,
        dateRangeColumnId,
    );

    const merged: FilterConfiguration = {
        revisionId: generateUuid(),
        // Clear-button preservation rules must not discard configured defaults on load.
        lineItems: [...baselineLineItems, ...persistedUserLineItems],
    };

    const withCreatedAtDefault = mergeDefaultCreatedAtMonthFilter(
        merged,
        dateRangeColumnId,
        applyDefaultCreatedAtMonthFilter,
    );

    const withPersistedCreatedAtDateRange =
        resolvedCreatedAtDateRange !== null
            ? applyCreatedAtDateRangeToFilters(withCreatedAtDefault, [
                  resolvedCreatedAtDateRange.from,
                  resolvedCreatedAtDateRange.to,
              ], dateRangeColumnId)
            : withCreatedAtDefault;

    if (journeyScopeFilterConfig === undefined || skipJourneyScopeReapply === true) {
        return withPersistedCreatedAtDateRange;
    }

    if (persistedJourneyScopeSelection !== undefined) {
        return applyJourneyScopeSelectionToFilterConfiguration(
            withPersistedCreatedAtDateRange,
            journeyScopeFilterConfig,
            persistedJourneyScopeSelection,
        );
    }

    const configDefaultJourneyScopeSelection = getJourneyScopeSelectionFromFilter(
        initialFilterConfiguration ?? {
            revisionId: generateUuid(),
            lineItems: [],
        },
        journeyScopeFilterConfig,
    );
    return applyJourneyScopeSelectionToFilterConfiguration(
        withPersistedCreatedAtDateRange,
        journeyScopeFilterConfig,
        configDefaultJourneyScopeSelection,
    );
}

function stripDateRangeFromVisibleFilterColumnIds(columnIds: Array<string>, dateRangeColumnId: string): Array<string> {
    // The date-range control owns this column; keep it out of other filter pills.
    return columnIds.filter((columnId) => columnId !== dateRangeColumnId);
}

function mergeVisibleFilterColumnIds(
    initialLineItems: Array<string>,
    persistedLineItems: Array<string>,
): Array<string> {
    const seen = new Set<string>();
    const merged: Array<string> = [];
    for (const columnId of [...initialLineItems, ...persistedLineItems]) {
        if (seen.has(columnId) === true) {
            continue;
        }
        seen.add(columnId);
        merged.push(columnId);
    }
    return merged;
}

export function resolveVisibleFiltersFromPersistence({
    dateRangeColumnId = DATA_VIEWER_CREATED_AT_DATE_RANGE_COLUMN_ID,
    persisted,
    initialVisibleFilters,
}: {
    dateRangeColumnId?: string;
    persisted: DataViewerFilterPersistenceV1 | null;
    initialVisibleFilters: {revisionId: string; lineItems: Array<string>} | undefined;
}): {revisionId: ReturnType<typeof generateUuid>; lineItems: Array<string>} {
    const initialLineItems =
        initialVisibleFilters !== undefined
            ? stripDateRangeFromVisibleFilterColumnIds(initialVisibleFilters.lineItems, dateRangeColumnId)
            : [];

    if (persisted !== null && persisted.visibleFilterColumnIds.length > 0) {
        const persistedLineItems = stripDateRangeFromVisibleFilterColumnIds(persisted.visibleFilterColumnIds, dateRangeColumnId);
        if (persistedLineItems.length > 0) {
            return {
                revisionId: generateUuid(),
                lineItems:
                    initialLineItems.length > 0
                        ? mergeVisibleFilterColumnIds(initialLineItems, persistedLineItems)
                        : persistedLineItems,
            };
        }
    }
    if (initialLineItems.length > 0) {
        return {
            revisionId: (initialVisibleFilters?.revisionId ?? generateUuid()) as ReturnType<typeof generateUuid>,
            lineItems: initialLineItems,
        };
    }
    return {
        revisionId: generateUuid(),
        lineItems: [],
    };
}

export function resolveSearchConfigurationFromPersistence({
    persisted,
    initialSearchConfiguration,
    searchColumns,
}: {
    persisted: DataViewerFilterPersistenceV1 | null;
    initialSearchConfiguration: SearchConfiguration | undefined;
    searchColumns: Array<string> | undefined;
}): SearchConfiguration {
    const base =
        initialSearchConfiguration ?? {
            revisionId: generateUuid(),
            searchValue: "",
            searchColumns: searchColumns ?? [],
        };
    if (persisted === null) {
        return base;
    }
    return {
        ...base,
        revisionId: generateUuid(),
        searchValue: persisted.searchValue,
    };
}

export function resolveSortConfigurationFromPersistence({
    persisted,
    initialSortConfiguration,
    ignorePersistedSort = false,
}: {
    persisted: DataViewerFilterPersistenceV1 | null;
    initialSortConfiguration: SortConfiguration | undefined;
    ignorePersistedSort?: boolean;
}): SortConfiguration | undefined {
    if (
        ignorePersistedSort === true ||
        persisted === null ||
        persisted.sortLineItems.length === 0
    ) {
        return initialSortConfiguration;
    }
    return {
        revisionId: generateUuid(),
        lineItems: persisted.sortLineItems,
    };
}
